import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import styled from "styled-components";
import { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useWriteSignedUrls } from "../hooks/useWriteSignedUrls";
import {
  useImageUploader,
  useWaitForImageUpload,
} from "@coworker/apprestructured/src/uploader/useImageUploader";
import { AddImagesModal } from "@coworker/apprestructured/src/task-management/components/AddImagesModal/AddImagesModal";
import { applyRotations } from "@coworker/apprestructured/src/task-management/util";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import { useSyncMediaImages } from "../hooks/useSyncMediaImages";
import { MAX_IMAGES_PER_MEDIA } from "../constants";

const FixedHeightContainer = styled.div`
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

interface AddImagesToNoImagesMediaProps {
  mediaId: string;
  groupId?: string;
}

export const AddImagesToNoImagesMedia = ({
  mediaId,
  groupId,
}: AddImagesToNoImagesMediaProps) => {
  const { t } = useTranslation();

  const [addImagesModalOpen, setAddImagesModalOpen] = useState(false);
  const [images, setImages] = useState([] as NewOrExistingFile[]);

  const useWriteSignedUrlsFunc = useMemo(() => useWriteSignedUrls, []);
  const imageSize = useMemo(() => ({ maxWidthOrHeight: 1024 }), []);

  const imageUploader = useImageUploader(
    images,
    imageSize,
    useWriteSignedUrlsFunc
  );

  const imageUploaderRef = useRef(imageUploader);
  imageUploaderRef.current = imageUploader;

  const { upload: uploadImages } = useWaitForImageUpload(
    () => imageUploaderRef.current
  );

  const { createUploads: imagesCreateUploads, error: imageUploaderError } =
    imageUploader;

  const { syncMediaImages, error: addMediaImagesError } = useSyncMediaImages({
    storedImages: [],
    currentImages: images,
    imagesCreateUploads,
  });

  const syncMediaImagesRef = useRef(syncMediaImages);
  syncMediaImagesRef.current = syncMediaImages;

  const addImagesHandler = async () => {
    const needsRotation = images.some((img) => img.rotation !== 0);
    if (needsRotation) {
      const rotatedImages = await applyRotations(images);
      flushSync(() => {
        setImages(rotatedImages);
      });
    }
    await uploadImages();
    await syncMediaImagesRef.current(mediaId, groupId);
  };

  const showSomethingWentWrongPrompt: boolean =
    !!imageUploaderError || !!addMediaImagesError;

  const handleRefreshClick = () => {
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  return (
    <FixedHeightContainer>
      <FixaText textStyle="Heading.L">{t("noImagesAddedYetString")}</FixaText>
      <FixaButton
        ssrIcon={ImageIconPath}
        onClick={() => setAddImagesModalOpen(true)}
        iconPosition="leading"
        text={t("addImagesString")}
        size="medium"
        type="primary"
      />
      <AddImagesModal
        isOpen={addImagesModalOpen}
        images={images}
        onImagesChange={setImages}
        onClose={() => setAddImagesModalOpen(false)}
        onConfirmClick={addImagesHandler}
        maxImages={MAX_IMAGES_PER_MEDIA}
      />
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </FixedHeightContainer>
  );
};
